import type { TFunction } from 'i18next';
import type { Climb } from '../../types/climb';
import type { Crag } from '../../types/crag';
import type { Zone, ZoneExposure } from '../../types/zone';
import { knownRouteTypes, ROUTE_TYPES, type RouteType } from '../../types/route-type';
import { isDrawableLine, lineThickness } from '../climbs/route-line';
import { formatGradeRange, gradeRanges, type GradeRange } from '../crags/grade-range';

/**
 * The crag sheet: one or more A4 pages listing a crag's routes, printed,
 * laminated and fixed to a post at the foot of the cliff. Everything here is
 * plain data and arithmetic — what goes on the sheet and where — so that the
 * PDF document only draws what it is handed, and the page that sets it up can
 * say "this will take 2 pages" without rendering anything.
 *
 * **The layout is ours, not react-pdf's.** react-pdf has no columns, and the
 * list reads down one column and then the next, the way a guidebook does. So
 * every row has a fixed height — one line, the name cut with an ellipsis — and
 * the columns and pages are filled here, by counting. The one block whose
 * height is not known in advance (the description, which wraps) is measured by
 * a first render and handed in as `headerHeight`.
 */

export type SheetLanguage = 'it' | 'en';
export const SHEET_LANGUAGES: SheetLanguage[] = ['it', 'en'];

/** By sector, numbered as along the rock; or every line A–Z. */
export type SheetGrouping = 'sector' | 'name';

/** Automatic: the largest type that fits `targetPages`. Manual: as given. */
export type SheetFit =
  | { mode: 'auto'; targetPages: number }
  | { mode: 'manual'; fontSize: number; columns: number };

export interface SheetSettings {
  language: SheetLanguage;
  grouping: SheetGrouping;
  fit: SheetFit;
  /** Sectors left off the sheet. Stored as excluded so a new sector is on by default. */
  excludedSectorIds: string[];
  /** The sectors' photos above the list, with the routes drawn and numbered on them. */
  showTopo: boolean;
  /** How many bolts each route has, beside its length. */
  showBolts: boolean;
  /** What kind of climbing each route is: its icons only, read against a legend. */
  showRouteTypes: boolean;
  /** The description as it goes on the sheet, per language; null = the crag's own. */
  descriptions: Record<SheetLanguage, string | null>;
}

export const DEFAULT_SETTINGS: SheetSettings = {
  language: 'it',
  grouping: 'sector',
  fit: { mode: 'auto', targetPages: 1 },
  excludedSectorIds: [],
  showTopo: true,
  showBolts: true,
  showRouteTypes: true,
  descriptions: { it: null, en: null },
};

// ---------------------------------------------------------------------------
// Page geometry, in PDF points (1/72 inch). A4 portrait.

export const PAGE_WIDTH = 595.28;
export const PAGE_HEIGHT = 841.89;
export const MARGIN_X = 32;
/** From the top edge to the first line of content, under the accent bar. */
export const MARGIN_TOP = 30;
/** Under the footer, so a punched hole or a cable tie does not land on it. */
export const MARGIN_BOTTOM = 20;
export const FOOTER_HEIGHT = 80;
/** Between the header block and the list on the first page. */
export const HEADER_GAP = 10;
/** Between the text of the header and the photos under it. */
export const TOPO_GAP = 8;
/** Between one photo and the next. */
export const TOPO_PHOTO_GAP = 6;
/** A 3:2 photo across the whole page is 354pt: it is cut down a little to leave the list its page. */
export const TOPO_MAX_HEIGHT = 320;
/** What the first page keeps for the list, however tall the description. */
export const MIN_LIST_HEIGHT = 150;
/** The crag's name repeated on top of every page after the first. */
export const RUNNING_HEADER_HEIGHT = 24;
export const COLUMN_GAP = 18;
export const CONTENT_WIDTH = PAGE_WIDTH - MARGIN_X * 2;

/** The type sizes the list may be set in. 7pt is about the smallest that reads at arm's length. */
export const MIN_FONT_SIZE = 7;
export const MAX_FONT_SIZE = 16;
const FONT_STEP = 0.5;
export const COLUMN_CHOICES = [2, 3, 4] as const;
export const PAGE_CHOICES = [1, 2, 3, 4] as const;
/** The manual sizes, as the page offers them. */
export const MANUAL_FONT_SIZES = { s: 8, m: 10, l: 12, xl: 14 } as const;

/** A route row: one line, a quarter of the size above and below. */
export function rowHeight(fontSize: number): number {
  return fontSize * 1.6;
}

/** A sector's heading: its name a little larger, air above, a rule below. */
export function headingHeight(fontSize: number): number {
  return fontSize * 2.4;
}

/** Which optional pieces a route row has; the name and the grade are always there. */
export interface SheetRowParts {
  number: boolean;
  sector: boolean;
  length: boolean;
  bolts: boolean;
  /** The most icons any route has. */
  routeTypes: number;
}

/** A route type's icon: a little taller than the type, to be read at all when it is small. */
export function routeTypeIconSize(fontSize: number): number {
  return fontSize * 1.1;
}

/**
 * How wide each piece of a route row is, in points — everything but the name,
 * which takes what is left. Shared by the layout, which uses it to refuse a
 * size that leaves the name no room, and by the PDF, which draws with it.
 */
export function rowWidths(fontSize: number, parts: SheetRowParts) {
  return {
    padding: fontSize * 0.3,
    number: parts.number ? fontSize * 1.9 : 0,
    numberGap: parts.number ? fontSize * 0.6 : 0,
    // The sector tells the lines apart in the A–Z list: short, cut if need be.
    sector: parts.sector ? fontSize * 6 : 0,
    sectorGap: parts.sector ? fontSize * 0.4 : 0,
    // Between the name's ellipsis and whatever follows it, which would
    // otherwise touch: "Per qualche Kg in…20 m".
    nameGap: fontSize * 0.5,
    // The widest row's, which is what a size has to leave the name enough room beside.
    routeTypes: parts.routeTypes * (routeTypeIconSize(fontSize) + routeTypeIconGap(fontSize)),
    length: parts.length ? fontSize * 2.6 : 0,
    // "12 bolts" in the small type the length is set in.
    bolts: parts.bolts ? fontSize * 3.2 : 0,
    grade: fontSize * 2.6,
  };
}

/** Between one route type's icon and the next. */
export function routeTypeIconGap(fontSize: number): number {
  return fontSize * 0.15;
}

/**
 * About thirteen characters. Fewer and ordinary names are cut to nothing; more
 * and a big crag never gets its fourth column, which is what lets it fit.
 */
const MIN_NAME_EMS = 6.5;

/** Whether a column this wide leaves the name enough room at this size. */
export function nameFits(fontSize: number, columns: number, parts: SheetRowParts): boolean {
  const w = rowWidths(fontSize, parts);
  const fixed = w.padding * 2 + w.number + w.numberGap + w.nameGap + w.sector + w.sectorGap + w.routeTypes + w.length + w.bolts + w.grade;
  return columnWidth(columns) - fixed >= fontSize * MIN_NAME_EMS;
}

export function columnWidth(columns: number): number {
  return (CONTENT_WIDTH - COLUMN_GAP * (columns - 1)) / columns;
}

/** The height the list has on a page: the first carries the header, the others a running title. */
export function listHeight(pageIndex: number, headerHeight: number): number {
  const above = pageIndex === 0 ? headerHeight + HEADER_GAP : RUNNING_HEADER_HEIGHT;
  return PAGE_HEIGHT - MARGIN_TOP - above - FOOTER_HEIGHT - MARGIN_BOTTOM;
}

// ---------------------------------------------------------------------------
// What goes on the sheet.

export interface SheetRoute {
  id: string;
  /** Its place along the sector, from 1; null in an A–Z list with no photo to read it against. */
  number: number | null;
  name: string;
  /** "?" when nobody has graded it. */
  grade: string;
  /** Metres, routes only, when somebody paced it out. */
  length: number | null;
  /** Null when it is not counted, or the sheet was asked to leave it out. */
  bolts: number | null;
  /** Empty when none is set, or the sheet was asked to leave them out. */
  routeTypes: RouteType[];
  /** The sector's name, for an A–Z list across several sectors. */
  sectorName: string | null;
}

export interface SheetSector {
  /** null for the routes filed under no sector. */
  id: string | null;
  name: string | null;
  exposure: ZoneExposure | null;
  routes: SheetRoute[];
}

export interface SheetStats {
  routes: number;
  /** Zero on a crag of one sector, which has none to count. */
  sectors: number;
  ranges: GradeRange[];
  /** The aspect of a crag's one sector, which has no heading left to carry it. */
  exposure: ZoneExposure | null;
}

/** A route on its sector's photo, under the number it has in the list. */
export interface SheetTopoLine {
  routeId: string;
  number: number;
  /** Fractions of the photo, as the line is stored. */
  points: { x: number; y: number }[];
  /** A fraction of the photo's long edge. */
  thickness: number;
}

/** A sector's photo as the sheet asks for it. */
export interface SheetTopo {
  sectorId: string;
  /** Null on a crag of one sector, where the photo needs no caption. */
  sectorName: string | null;
  imageUrl: string;
  lines: SheetTopoLine[];
}

/** A photo as react-pdf can draw it: a JPEG data URL, with its size for its shape. */
export interface SheetPicture {
  src: string;
  width: number;
  height: number;
}

/** A sector's photo once read, ready for the PDF. */
export type SheetDrawnTopo = Omit<SheetTopo, 'imageUrl'> & { picture: SheetPicture };

export interface SheetContent {
  cragName: string;
  description: string;
  stats: SheetStats;
  /** One group per sector; a single nameless group in the A–Z list. */
  sectors: SheetSector[];
  grouping: SheetGrouping;
  /** All the crag's printable lines are on one wall: nothing on the sheet names it. */
  singleSector: boolean;
  /** The length is left out when no line has one, the number when nothing refers to it. */
  rowParts: SheetRowParts;
  /** Every route type on the sheet, for the legend that says what its icons mean. */
  routeTypes: RouteType[];
  /** The sectors' photos above the list; empty when turned off or when there are none. */
  topos: SheetTopo[];
}

/**
 * The lines that belong on a sign at the cliff: the crag's own, confirmed and
 * not taken down. A member's proposed line waits for the staff before it is
 * printed on anything.
 */
export function printableClimbs(climbs: Climb[]): Climb[] {
  return climbs.filter(
    (climb) => climb.official_status === 'confirmed' && !climb.discarded_at && climb.type !== 'BoardClimb'
  );
}

/**
 * Whether a route's line was traced on its sector's photo. A member adding a
 * route from the crag page traces it on a photo of their own, and those
 * coordinates over the cliff's photo are a scribble in the wrong place, which on
 * a laminated sign nobody can tap away. A route that shares its sector's picture
 * has the same address, and one with no picture has nothing to contradict it.
 */
function lineFitsSectorPhoto(climb: Climb, sector: Zone): boolean {
  return !climb.image_url || climb.image_url === sector.image_url;
}

function climbLength(climb: Climb): number | null {
  if (climb.type !== 'Route') return null;
  const length = (climb as { length?: unknown }).length;
  return typeof length === 'number' && length > 0 ? length : null;
}

/** The description the sheet starts from in a language: the crag's own, else the other language's. */
export function cragDescription(crag: Crag, language: SheetLanguage): string {
  const own = language === 'it' ? crag.description_it : crag.description_en;
  const other = language === 'it' ? crag.description_en : crag.description_it;
  return (own || other || crag.description || '').trim();
}

/**
 * Everything the sheet says, from the crag, its sectors and its lines.
 *
 * The lines arrive in the order the crag page lists them (by position along
 * the rock), which is the order they are numbered in. Sectors follow their
 * own position; the lines filed under none come last, under "Other routes".
 */
export function buildSheetContent(
  crag: Crag,
  sectors: Zone[],
  climbs: Climb[],
  settings: SheetSettings
): SheetContent {
  const excluded = new Set(settings.excludedSectorIds);
  const sortedSectors = [...sectors].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const known = new Set(sortedSectors.map((sector) => sector.id));
  const sectorName = new Map(sortedSectors.map((sector) => [sector.id, sector.name]));

  const sectorKey = (climb: Climb): string | null => {
    const id = climb.zone?.id ?? null;
    return id && known.has(id) ? id : null;
  };

  const printable = printableClimbs(climbs);
  const selected = printable.filter((climb) => !excluded.has(sectorKey(climb) ?? UNSECTORED_ID));
  // Asked of the whole crag rather than of what is ticked: a sheet made for one
  // wall of a larger crag still has to say which wall it is.
  const singleSector = new Set(printable.map(sectorKey)).size <= 1;

  const groups = new Map<string | null, Climb[]>();
  for (const climb of selected) {
    const key = sectorKey(climb);
    const group = groups.get(key);
    if (group) group.push(climb);
    else groups.set(key, [climb]);
  }

  const placeAlongSector = new Map<string, number>();
  for (const group of groups.values()) {
    group.forEach((climb, index) => placeAlongSector.set(climb.id, index + 1));
  }

  const topos: SheetTopo[] = settings.showTopo
    ? sortedSectors.flatMap((sector) => {
        const group = groups.get(sector.id);
        if (!sector.image_url || !group) return [];
        return [
          {
            sectorId: sector.id,
            sectorName: singleSector ? null : sector.name,
            imageUrl: sector.image_url,
            lines: group.flatMap((climb) =>
              isDrawableLine(climb.line) && lineFitsSectorPhoto(climb, sector)
                ? [
                    {
                      routeId: climb.id,
                      number: placeAlongSector.get(climb.id)!,
                      points: climb.line.points,
                      thickness: lineThickness(climb.line),
                    },
                  ]
                : []
            ),
          },
        ];
      })
    : [];
  // The numbers on a photo are the ones in the list, in either order of it.
  const numbered = settings.grouping === 'sector' || topos.length > 0;

  const route = (climb: Climb) => (climb.type === 'Route' ? climb : null);
  const toRoute = (climb: Climb): SheetRoute => {
    const key = sectorKey(climb);
    return {
      id: climb.id,
      number: numbered ? placeAlongSector.get(climb.id)! : null,
      name: climb.name || climb.display_name || '—',
      grade: climb.difficulty || '?',
      length: climbLength(climb),
      bolts: settings.showBolts ? (route(climb)?.bolts ?? null) : null,
      routeTypes: settings.showRouteTypes ? knownRouteTypes(route(climb)?.route_types) : [],
      sectorName: key ? sectorName.get(key) ?? null : null,
    };
  };

  let sheetSectors: SheetSector[];
  if (settings.grouping === 'sector') {
    sheetSectors = [...sortedSectors, null]
      .map((sector) => {
        const group = groups.get(sector?.id ?? null) ?? [];
        return {
          id: sector?.id ?? null,
          name: sector?.name ?? null,
          exposure: sector?.exposure ?? null,
          routes: group.map(toRoute),
        };
      })
      .filter((sector) => sector.routes.length > 0);
  } else {
    const collator = new Intl.Collator(settings.language, { sensitivity: 'base', numeric: true });
    const routes = selected.map(toRoute).sort((a, b) => collator.compare(a.name, b.name));
    sheetSectors = routes.length > 0 ? [{ id: null, name: null, exposure: null, routes }] : [];
  }

  const draft = settings.descriptions[settings.language];
  const routes = sheetSectors.flatMap((sector) => sector.routes);

  return {
    cragName: crag.name,
    description: (draft ?? cragDescription(crag, settings.language)).trim(),
    stats: {
      routes: selected.length,
      sectors: singleSector ? 0 : new Set(selected.map(sectorKey).filter((key) => key !== null)).size,
      ranges: gradeRanges(selected),
      exposure: singleSector ? (sheetSectors[0]?.exposure ?? null) : null,
    },
    sectors: sheetSectors,
    grouping: settings.grouping,
    singleSector,
    rowParts: {
      number: numbered,
      sector: settings.grouping === 'name' && !singleSector,
      length: routes.some((route) => route.length !== null),
      bolts: routes.some((route) => route.bolts !== null),
      routeTypes: Math.max(0, ...routes.map((route) => route.routeTypes.length)),
    },
    routeTypes: ROUTE_TYPES.filter((type) => routes.some((route) => route.routeTypes.includes(type))),
    topos,
  };
}

/** The key the sectors' checkboxes use for the lines filed under none. */
export const UNSECTORED_ID = '__unsectored__';


/** "84 vie · 6 settori · 5a – 8b", or with two ranges for a crag of routes and boulders. */
export function statsLine(stats: SheetStats, t: TFunction): string {
  const parts = [t('cragSheet.pdf.routesCount', { count: stats.routes })];
  if (stats.sectors > 0) parts.push(t('cragSheet.pdf.sectorsCount', { count: stats.sectors }));
  if (stats.ranges.length === 1) {
    parts.push(formatGradeRange(stats.ranges[0]));
  } else {
    for (const range of stats.ranges) {
      parts.push(`${t(range.type === 'Route' ? 'cragSheet.pdf.routes' : 'cragSheet.pdf.boulders')} ${formatGradeRange(range)}`);
    }
  }
  if (stats.exposure) parts.push(t(`cragInfo.exposures.${stats.exposure}`));
  return parts.join('  ·  ');
}

// ---------------------------------------------------------------------------
// Laying the list out.

export type SheetItem =
  | { kind: 'heading'; name: string | null; exposure: ZoneExposure | null; continued: boolean }
  | { kind: 'route'; route: SheetRoute };

export interface SheetPage {
  columns: SheetItem[][];
}

export interface SheetLayout {
  fontSize: number;
  columns: number;
  pages: SheetPage[];
  /** False when even the smallest type on four columns runs past the pages asked for. */
  fits: boolean;
}

/** Whether a sector gets a heading at all: the A–Z list and a crag of one sector or none do not. */
function hasHeadings(content: SheetContent): boolean {
  return (
    content.grouping === 'sector' && !content.singleSector && content.sectors.some((sector) => sector.name !== null)
  );
}

/** A cap on the columns' height from one page on: how the last page is balanced. */
interface ColumnCap {
  fromPage: number;
  height: number;
}

/**
 * The list poured into columns and pages, at one type size.
 *
 * A sector's heading never ends a column on its own — it moves to the next
 * one with its first line — and a sector that runs over into a new column
 * gets its heading again, marked as continued, so a column read on its own
 * still says which wall it is on.
 */
function pour(
  content: SheetContent,
  fontSize: number,
  columns: number,
  headerHeight: number,
  cap?: ColumnCap
): SheetPage[] {
  const row = rowHeight(fontSize);
  const heading = headingHeight(fontSize);
  const headings = hasHeadings(content);

  const pages: SheetPage[] = [{ columns: [[]] }];
  let used = 0;
  const capacity = () => {
    const index = pages.length - 1;
    const full = listHeight(index, headerHeight);
    return cap && index >= cap.fromPage ? Math.min(full, cap.height) : full;
  };
  const current = () => {
    const page = pages[pages.length - 1];
    return page.columns[page.columns.length - 1];
  };
  const nextColumn = () => {
    const page = pages[pages.length - 1];
    if (page.columns.length < columns) page.columns.push([]);
    else pages.push({ columns: [[]] });
    used = 0;
  };

  for (const sector of content.sectors) {
    sector.routes.forEach((route, index) => {
      const first = index === 0;
      const needsHeading = () => headings && (first || current().length === 0);
      if (used + row + (needsHeading() ? heading : 0) > capacity() && current().length > 0) nextColumn();
      if (needsHeading()) {
        current().push({ kind: 'heading', name: sector.name, exposure: sector.exposure, continued: !first });
        used += heading;
      }
      current().push({ kind: 'route', route });
      used += row;
    });
  }

  return pages;
}

/**
 * The last page with its columns evened out. Poured as is, a short last page
 * is full columns and a stub (or nothing) beside them; here its columns are
 * made as short as they can be while the lines still end on the same page. The pages
 * before it are filled exactly as before.
 */
function balanceLastPage(
  content: SheetContent,
  fontSize: number,
  columns: number,
  headerHeight: number,
  pages: SheetPage[]
): SheetPage[] {
  const lastIndex = pages.length - 1;
  if (columns < 2) return pages;

  let low = 0;
  let high = listHeight(lastIndex, headerHeight);
  let best = pages;
  // Half a point is finer than a row; 20 halvings of a page are far finer.
  for (let i = 0; i < 20 && high - low > 0.5; i += 1) {
    const mid = (low + high) / 2;
    const attempt = pour(content, fontSize, columns, headerHeight, { fromPage: lastIndex, height: mid });
    if (attempt.length === pages.length) {
      best = attempt;
      high = mid;
    } else {
      low = mid;
    }
  }
  return best;
}

/** The list laid out at one size, last page balanced. */
export function layoutAt(
  content: SheetContent,
  fontSize: number,
  columns: number,
  headerHeight: number
): SheetPage[] {
  const pages = pour(content, fontSize, columns, headerHeight);
  return balanceLastPage(content, fontSize, columns, headerHeight, pages);
}

/**
 * The sheet's layout for the settings.
 *
 * Automatic: the largest type, on as few columns as carry it, whose list ends
 * within the pages asked for. Ties go to fewer columns — wider columns cut
 * fewer names. If nothing fits, the smallest type on the most columns, and
 * `fits` says so.
 */
export function layoutSheet(content: SheetContent, fit: SheetFit, headerHeight: number): SheetLayout {
  if (fit.mode === 'manual') {
    const pages = layoutAt(content, fit.fontSize, fit.columns, headerHeight);
    return { fontSize: fit.fontSize, columns: fit.columns, pages, fits: true };
  }

  for (let size = MAX_FONT_SIZE; size >= MIN_FONT_SIZE; size -= FONT_STEP) {
    for (const columns of COLUMN_CHOICES) {
      if (!nameFits(size, columns, content.rowParts)) continue;
      const pages = pour(content, size, columns, headerHeight);
      if (pages.length <= fit.targetPages) {
        return {
          fontSize: size,
          columns,
          pages: balanceLastPage(content, size, columns, headerHeight, pages),
          fits: true,
        };
      }
    }
  }

  const columns = COLUMN_CHOICES[COLUMN_CHOICES.length - 1];
  const pages = layoutAt(content, MIN_FONT_SIZE, columns, headerHeight);
  return { fontSize: MIN_FONT_SIZE, columns, pages, fits: false };
}

// ---------------------------------------------------------------------------
// The photos above the list.

/** The height the photos may take under a text header this tall. */
export function topoRoom(textHeight: number): number {
  const page = PAGE_HEIGHT - MARGIN_TOP - FOOTER_HEIGHT - MARGIN_BOTTOM - HEADER_GAP;
  return Math.min(TOPO_MAX_HEIGHT, page - textHeight - TOPO_GAP - MIN_LIST_HEIGHT);
}

export interface TopoBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TopoLayout {
  height: number;
  boxes: TopoBox[];
}

const MAX_TOPOS_PER_ROW = 3;

/**
 * The photos in rows across the page, each at its own shape, a row as tall as
 * makes it fill the width. Tried at one, two and three to a row: the one that
 * leaves the photos the most area wins, because a wall drawn small is a wall
 * whose numbers cannot be told apart. Past `maxHeight` everything is scaled down
 * together and each row centred.
 */
export function layoutTopos(ratios: number[], maxHeight: number): TopoLayout {
  let best: TopoLayout = { height: 0, boxes: [] };
  let bestArea = -1;

  for (let perRow = 1; perRow <= Math.min(MAX_TOPOS_PER_ROW, ratios.length); perRow += 1) {
    const rows: number[][] = [];
    for (let i = 0; i < ratios.length; i += perRow) rows.push(ratios.slice(i, i + perRow));

    const heights = rows.map((row) => {
      const room = CONTENT_WIDTH - TOPO_PHOTO_GAP * (row.length - 1);
      return room / row.reduce((sum, ratio) => sum + ratio, 0);
    });
    const gaps = TOPO_PHOTO_GAP * (rows.length - 1);
    const natural = heights.reduce((sum, height) => sum + height, 0);
    const scale = Math.min(1, (maxHeight - gaps) / natural);

    let y = 0;
    let area = 0;
    const boxes: TopoBox[] = [];
    rows.forEach((row, index) => {
      const height = heights[index] * scale;
      const rowWidth =
        row.reduce((sum, ratio) => sum + ratio * height, 0) + TOPO_PHOTO_GAP * (row.length - 1);
      let x = (CONTENT_WIDTH - rowWidth) / 2;
      for (const ratio of row) {
        boxes.push({ x, y, width: ratio * height, height });
        area += ratio * height * height;
        x += ratio * height + TOPO_PHOTO_GAP;
      }
      y += height + TOPO_PHOTO_GAP;
    });

    if (area > bestArea) {
      bestArea = area;
      best = { height: y - TOPO_PHOTO_GAP, boxes };
    }
  }
  return best;
}

/** How large a route's number is drawn on a photo this wide. */
export function badgeRadius(photoWidth: number): number {
  return Math.min(7, Math.max(4.5, photoWidth * 0.0125));
}

export interface PlacedBadge {
  number: number;
  /** Where the route starts, in points on the photo. */
  foot: { x: number; y: number };
  /** Where its number sits: the foot, unless another number is there first. */
  x: number;
  y: number;
}

/** The lower end of a line: its foot, whichever way round it was traced. */
function lineFoot(points: SheetTopoLine['points']) {
  const first = points[0];
  const last = points[points.length - 1];
  return last.y > first.y ? last : first;
}

/**
 * Each route's number at the foot of its line. Routes on a cliff often start a
 * metre apart, so numbers that would overlap are pushed apart sideways, the way
 * they are along the foot of a wall, and stay on the photo.
 */
export function placeBadges(lines: SheetTopoLine[], width: number, height: number, radius: number): PlacedBadge[] {
  const clampX = (x: number) => Math.min(width - radius, Math.max(radius, x));
  const clampY = (y: number) => Math.min(height - radius, Math.max(radius, y));
  const spacing = radius * 2 + 1;

  const badges = lines
    .map((line) => {
      const foot = lineFoot(line.points);
      const at = { x: foot.x * width, y: foot.y * height };
      return { number: line.number, foot: at, x: clampX(at.x), y: clampY(at.y) };
    })
    .sort((a, b) => a.x - b.x || a.number - b.number);

  // Pushing one pair apart can crowd the next, so it takes a few passes.
  for (let pass = 0; pass < 200; pass += 1) {
    let moved = false;
    for (let i = 0; i < badges.length; i += 1) {
      for (let j = i + 1; j < badges.length; j += 1) {
        const dy = badges[j].y - badges[i].y;
        if (Math.abs(dy) >= spacing) continue;
        const needed = Math.sqrt(spacing * spacing - dy * dy);
        const overlap = needed - (badges[j].x - badges[i].x);
        if (overlap <= 0.01) continue;
        // Half each, and against the photo's edge the other takes it all.
        const left = clampX(badges[i].x - overlap / 2);
        const right = clampX(badges[j].x + overlap - (badges[i].x - left));
        badges[i].x = clampX(right - needed);
        badges[j].x = right;
        moved = true;
      }
    }
    if (!moved) break;
  }
  return badges;
}

// ---------------------------------------------------------------------------
// Links and names.

/**
 * Where the code on the sheet goes: the crag's page, tagged so a visit that
 * came from the sign can be told apart from the others.
 */
export function sheetUrl(origin: string, slug: string): string {
  return `${origin}/crags/${slug}?ref=sheet`;
}

/** The same address as a person would type it: no scheme, no `www.`, no tag. */
export function printableUrl(origin: string, slug: string): string {
  return `${origin.replace(/^https?:\/\//, '').replace(/^www\./, '')}/crags/${slug}`;
}

export function sheetFileName(slug: string, language: SheetLanguage): string {
  return `${slug}-${language === 'it' ? 'scheda' : 'sheet'}.pdf`;
}
